// Angebote, Auftragsbestätigungen, Lieferscheine und Gutschriften.
//
// Eine Seite mit vier Reitern statt vier Menüpunkten: es ist derselbe Vorgang
// in verschiedenen Stadien, und der Weg von einem Beleg zum nächsten soll ein
// Klick sein, kein Seitenwechsel.
import { apiUrl } from '../../../gemeinsam/lib/plattform';
import { Erfolgsmeldung } from '../../../gemeinsam/komponenten/Meldungen';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api, ApiFehler } from '../../../gemeinsam/lib/api';
import { useDaten, euro, datum, profil, menge, preis } from '../../../gemeinsam/lib/daten';
import { belegOeffnen } from '../lib/drucken';
import type { Artikel, Kunde } from '../../../gemeinsam/lib/typen';
import {
  Abzeichen, Auswahl, Feld, Hinweis, Karte, KartenKopf, Knopf, LeerZustand, Spinner,
} from '../../../gemeinsam/komponenten/Basis';
import { useSprache } from '../../../gemeinsam/lib/i18n';
import { Fenster } from '../../../gemeinsam/komponenten/Fenster';
import {
  leerePosition, PositionenEditor, positionenFuerApi, summeAusEntwurf,
  type Entwurfsposition,
} from '../komponenten/Positionen';

type Art = 'angebot' | 'auftrag' | 'lieferschein' | 'gutschrift';

type Beleg = {
  id: string;
  art: Art;
  nummer: string | null;
  kundeId: string | null;
  kundeName: string;
  positionen: Array<{ bezeichnung: string; menge: number; einheit: string; einzelpreis: number; netto: number }>;
  netto: number;
  steuer: number;
  brutto: number;
  status: string;
  notiz: string;
  datum: string;
  gueltigBis: string | null;
  lieferdatum: string | null;
  herkunft: { art: string; belegId: string; nummer: string | null } | null;
  nachfolger: Array<{ art: string; belegId: string; nummer: string | null }>;
  rechnungId: string | null;
};

// `neu` trägt den Artikel mit: „Neues Angebot", aber „Neuer Lieferschein".
// Klarnamen für Verweise auf andere Belegarten — auch für die Rechnung, die
// selbst kein Reiter ist.
const BELEGNAME: Record<string, string> = {
  angebot: 'Angebot', auftrag: 'Auftragsbestätigung',
  lieferschein: 'Lieferschein', gutschrift: 'Gutschrift', rechnung: 'Rechnung',
};

const REITER: Array<{ art: Art; titel: string; einzahl: string; neu: string }> = [
  { art: 'angebot', titel: 'Angebote', einzahl: 'Angebot', neu: 'Neues Angebot' },
  { art: 'auftrag', titel: 'Aufträge', einzahl: 'Auftragsbestätigung', neu: 'Neue Auftragsbestätigung' },
  { art: 'lieferschein', titel: 'Lieferscheine', einzahl: 'Lieferschein', neu: 'Neuer Lieferschein' },
  { art: 'gutschrift', titel: 'Gutschriften', einzahl: 'Gutschrift', neu: 'Neue Gutschrift' },
];

// Welcher Status wie aussieht und wie er heißt.
const STATUS: Record<string, { text: string; farbe: 'neutral' | 'gruen' | 'rot' | 'gelb' | 'blau' }> = {
  entwurf: { text: 'Entwurf', farbe: 'neutral' },
  gesendet: { text: 'Gesendet', farbe: 'blau' },
  angenommen: { text: 'Angenommen', farbe: 'gruen' },
  abgelehnt: { text: 'Abgelehnt', farbe: 'rot' },
  abgelaufen: { text: 'Abgelaufen', farbe: 'neutral' },
  offen: { text: 'Offen', farbe: 'gelb' },
  inArbeit: { text: 'In Arbeit', farbe: 'blau' },
  erledigt: { text: 'Erledigt', farbe: 'gruen' },
  geliefert: { text: 'Geliefert', farbe: 'gruen' },
  gestellt: { text: 'Gestellt', farbe: 'gruen' },
  storniert: { text: 'Storniert', farbe: 'rot' },
};

// Welche Statuswechsel der Anwender je Belegart anbieten darf.
const NAECHSTE_SCHRITTE: Record<Art, Record<string, Array<{ status: string; text: string; gefahr?: boolean }>>> = {
  angebot: {
    entwurf: [{ status: 'gesendet', text: 'Als gesendet markieren' }],
    gesendet: [
      { status: 'angenommen', text: 'Angenommen' },
      { status: 'abgelehnt', text: 'Abgelehnt', gefahr: true },
    ],
  },
  auftrag: {
    offen: [{ status: 'inArbeit', text: 'In Arbeit' }, { status: 'storniert', text: 'Stornieren', gefahr: true }],
    inArbeit: [{ status: 'erledigt', text: 'Erledigt' }, { status: 'storniert', text: 'Stornieren', gefahr: true }],
  },
  lieferschein: {
    entwurf: [{ status: 'geliefert', text: 'Als geliefert buchen' }],
    geliefert: [{ status: 'storniert', text: 'Stornieren', gefahr: true }],
  },
  gutschrift: {
    entwurf: [{ status: 'gestellt', text: 'Gutschrift stellen' }],
    gestellt: [{ status: 'storniert', text: 'Stornieren', gefahr: true }],
  },
};

// Was aus welchem Beleg werden kann.
const UMWANDLUNGEN: Record<Art, Array<{ ziel: string; text: string }>> = {
  angebot: [
    { ziel: 'auftrag', text: 'Auftrag daraus machen' },
    { ziel: 'lieferschein', text: 'Lieferschein' },
    { ziel: 'rechnung', text: 'Direkt berechnen' },
  ],
  auftrag: [
    { ziel: 'lieferschein', text: 'Lieferschein erzeugen' },
    { ziel: 'rechnung', text: 'Rechnung erzeugen' },
  ],
  lieferschein: [{ ziel: 'rechnung', text: 'Rechnung erzeugen' }],
  gutschrift: [],
};

export default function Belege() {
  const { t } = useSprache();
  const ort = useLocation();
  const [art, setArt] = useState<Art>(() => {
    const p = new URLSearchParams(ort.search);
    const gewuenscht = p.get('art') || p.get('neu');
    return REITER.some(r => r.art === gewuenscht) ? gewuenscht as Art : 'angebot';
  });
  const { daten: liste, laedt, neuLaden } = useDaten<Beleg[]>(`/belege?art=${art}`, [art]);
  const { daten: uebersicht, neuLaden: uebersichtNeu } = useDaten<{
    angebote: { offen: number; wert: number };
    auftraege: { offen: number; wert: number };
    lieferscheine: { offen: number };
    gutschriften: { wert: number };
  }>('/belege/uebersicht');
  const { daten: kunden } = useDaten<Kunde[]>('/customers');
  const { daten: artikelDaten } = useDaten<{ produkte: Artikel[] }>('/products');

  const [neu, setNeu] = useState<{ kundeId: string; kundeName: string; positionen: Entwurfsposition[]; rabatt: string; notiz: string } | null>(null);
  const [offen, setOffen] = useState<Beleg | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [sendet, setSendet] = useState(false);

  // Aus der Kundenakte: ?art=…&id= öffnet den Beleg, ?neu=<art>&kunde= legt
  // einen für diesen Kunden an.
  useEffect(() => {
    const p = new URLSearchParams(ort.search);
    const gewuenscht = (p.get('art') || p.get('neu')) as Art | null;
    if (gewuenscht && REITER.some(r => r.art === gewuenscht)) setArt(gewuenscht);
    const id = p.get('id');
    if (id) api<Beleg>(`/belege/${id}`).then(setOffen).catch(() => setFehler('Beleg nicht gefunden.'));
    if (p.get('neu') && p.get('kunde')) {
      setNeu({ kundeId: p.get('kunde') || '', kundeName: '', positionen: [leerePosition()], rabatt: '0', notiz: '' });
    }
  }, [ort.search]);

  const artikel = artikelDaten?.produkte ?? [];
  const reiter = REITER.find(r => r.art === art)!;
  const mitPreisen = art !== 'lieferschein';

  async function alles() { await Promise.all([neuLaden(), uebersichtNeu()]); }

  async function drucken(b: Beleg) {
    const problem = await belegOeffnen(apiUrl(`/api/belege/${b.id}/druck`));
    if (problem) setFehler(problem);
  }

  async function anlegen() {
    if (!neu) return;
    setFehler(null);
    setSendet(true);
    try {
      const positionen = positionenFuerApi(neu.positionen);
      if (positionen.length === 0) throw new ApiFehler(400, 'Bitte mindestens eine Position erfassen.');
      await api('/belege', {
        methode: 'POST',
        daten: {
          art,
          kundeId: neu.kundeId || undefined,
          kundeName: neu.kundeId ? undefined : neu.kundeName,
          rabattProzent: Number(neu.rabatt.replace(',', '.')) || 0,
          notiz: neu.notiz,
          positionen,
        },
      });
      setNeu(null);
      setMeldung(`${reiter.einzahl} angelegt.`);
      await alles();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : `${reiter.einzahl} konnte nicht angelegt werden.`);
    } finally { setSendet(false); }
  }

  async function statusSetzen(b: Beleg, status: string, gefahr?: boolean) {
    if (gefahr && !confirm(
      `${reiter.einzahl} ${b.nummer || '(Entwurf)'} wirklich auf „${STATUS[status]?.text || status}" setzen?`
      + (b.art === 'lieferschein' && b.status === 'geliefert' ? '\n\nDie Ware wird ins Lager zurückgebucht.' : ''))) return;
    setFehler(null);
    try {
      const aktualisiert = await api<Beleg>(`/belege/${b.id}/status`, { methode: 'POST', daten: { status } });
      setOffen(aktualisiert);
      setMeldung(`${reiter.einzahl} ${aktualisiert.nummer || ''} ist jetzt ${STATUS[status]?.text || status}.`);
      await alles();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Statuswechsel fehlgeschlagen.');
    }
  }

  async function umwandeln(b: Beleg, ziel: string) {
    setFehler(null);
    try {
      // Wie beim Stellen von Hand: zur Rechnung gleich der Lieferschein — außer
      // sie entsteht aus einem Lieferschein, dann ist es dieser.
      const antwort = await api<{ neu: { id: string; nummer: string | null; lieferscheinId?: string }; art: string }>(
        `/belege/${b.id}/umwandeln`, {
          methode: 'POST', daten: { ziel, lieferschein: ziel === 'rechnung' && b.art !== 'lieferschein' },
        });
      setOffen(null);
      setMeldung(ziel === 'rechnung'
        ? `Rechnung ${antwort.neu.nummer} erstellt${antwort.neu.lieferscheinId && b.art !== 'lieferschein' ? ', Lieferschein dazu' : ''}. `
          + 'Beides steht unter „Rechnungen" zum Drucken bereit.'
        : `${antwort.neu.nummer} erstellt.`);
      if (ziel !== 'rechnung') setArt(ziel as Art);
      await alles();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Umwandlung fehlgeschlagen.');
    }
  }

  async function loeschen(b: Beleg) {
    if (!confirm('Diesen Entwurf löschen?')) return;
    try {
      await api(`/belege/${b.id}`, { methode: 'DELETE' });
      setOffen(null);
      setMeldung('Entwurf gelöscht.');
      await alles();
    } catch (e) {
      setFehler(e instanceof ApiFehler ? e.message : 'Löschen fehlgeschlagen.');
    }
  }

  const entwurfSumme = neu ? summeAusEntwurf(neu.positionen, Number(neu.rabatt.replace(',', '.')) || 0) : null;

  return (
    <div className="mx-auto max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{t('belege.titel')}</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            Vom Angebot bis zur Gutschrift — jeder Schritt baut auf dem vorigen auf.
          </p>
        </div>
        {!neu && (
          <Knopf variante="primaer" onClick={() => setNeu({
            kundeId: '', kundeName: '', positionen: [leerePosition()], rabatt: '0', notiz: '',
          })}>
            {reiter.einzahl} anlegen
          </Knopf>
        )}
      </div>

      {uebersicht && (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <Kennzahl titel={t('belege.offeneAngebote')} wert={String(uebersicht.angebote.offen)} zusatz={euro(uebersicht.angebote.wert)} />
          <Kennzahl titel={t('belege.laufendeAuftraege')} wert={String(uebersicht.auftraege.offen)} zusatz={euro(uebersicht.auftraege.wert)} />
          <Kennzahl titel={t('belege.lieferscheineOffen')} wert={String(uebersicht.lieferscheine.offen)} zusatz="noch nicht geliefert" />
          <Kennzahl titel={t('belege.gutschriften')} wert={euro(uebersicht.gutschriften.wert)} zusatz="gestellt" />
        </div>
      )}

      <div className="flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 dark:bg-slate-900">
        {REITER.map(r => (
          <button key={r.art} onClick={() => { setArt(r.art); setNeu(null); setOffen(null); }}
                  className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition ${art === r.art
                    ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-800 dark:text-slate-100'
                    : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200'}`}>
            {r.titel}
          </button>
        ))}
      </div>

      {fehler && !offen && <Hinweis art="fehler">{fehler}</Hinweis>}
      <Erfolgsmeldung text={meldung} />

      {art === 'gutschrift' && !neu && (
        <Hinweis art="info">
          Gutschriften entstehen meist aus einer Rechnung oder Retoure — dort finden Sie den Knopf
          „Gutschrift erstellen". Die Positionen werden dann übernommen.
        </Hinweis>
      )}

      {neu && (
        <Karte klasse="border-marke-300 dark:border-marke-800">
          <KartenKopf titel={reiter.neu}
                      hinweis={mitPreisen ? undefined : 'Lieferscheine tragen keine Preise'} />
          <div className="space-y-4 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Auswahl label={t('feld.kunde')} value={neu.kundeId}
                       onChange={e => setNeu({ ...neu, kundeId: e.target.value })}>
                <option value="">{art === 'angebot' ? '— Interessent ohne Kundenakte —' : 'Kunde wählen …'}</option>
                {(kunden ?? []).map(k => <option key={k.id} value={k.id}>{k.name}</option>)}
              </Auswahl>
              {!neu.kundeId && art === 'angebot' && (
                <Feld label={t('belege.interessent')} value={neu.kundeName}
                      onChange={e => setNeu({ ...neu, kundeName: e.target.value })} />
              )}
              {mitPreisen && (
                <Feld label={t('belege.rabatt')} value={neu.rabatt} inputMode="decimal"
                      onChange={e => setNeu({ ...neu, rabatt: e.target.value })} />
              )}
            </div>

            <PositionenEditor positionen={neu.positionen} artikel={artikel} mitPreisen={mitPreisen}
                              setzen={p => setNeu({ ...neu, positionen: p })} />

            <Feld label={t('belege.notiz')} value={neu.notiz}
                  onChange={e => setNeu({ ...neu, notiz: e.target.value })} />

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-4 dark:border-slate-800">
              {mitPreisen && entwurfSumme && (
                <div>
                  <p className="text-xs text-slate-500 dark:text-slate-400">{t('belege.gesamtBrutto')}</p>
                  <p className="text-xl font-bold text-slate-900 dark:text-slate-100">{euro(entwurfSumme.brutto)}</p>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Knopf variante="primaer" laedt={sendet} onClick={anlegen}>{reiter.einzahl} anlegen</Knopf>
                <Knopf onClick={() => { setNeu(null); setFehler(null); }}>{t('aktion.abbrechen')}</Knopf>
              </div>
            </div>
          </div>
        </Karte>
      )}

      {offen && (
        <Fenster
          beiSchliessen={() => { setOffen(null); setFehler(null); }}
          titel={`${reiter.einzahl} ${offen.nummer || '(Entwurf)'}`}
          unterzeile={`${offen.kundeName} · ${datum(offen.datum)}`}
          aktionen={offen.kundeId && (
            <Link to={`/kunden/${offen.kundeId}`} className="rounded-lg px-3 py-2 text-sm font-medium text-marke-700 hover:bg-marke-50 dark:text-marke-300 dark:hover:bg-marke-950/40">
              {t('kunden.akte')}
            </Link>
          )}
        >
          <div className="space-y-4 p-5">
            {/* Fehler hier wiederholen — oben auf der Seite liegen sie hinter dem Fenster. */}
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
            {offen.herkunft?.nummer && (
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Entstanden aus {BELEGNAME[offen.herkunft.art] ?? offen.herkunft.art} {offen.herkunft.nummer}
              </p>
            )}

            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="pb-2">{t('feld.position')}</th>
                  <th className="pb-2 text-right">{t('feld.menge')}</th>
                  {mitPreisen && <><th className="pb-2 text-right">{t('belege.einzel')}</th><th className="pb-2 text-right">{t('feld.netto')}</th></>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {offen.positionen.map((p, i) => (
                  <tr key={i}>
                    <td className="py-2 text-slate-900 dark:text-slate-100">{p.bezeichnung}</td>
                    <td className="py-2 text-right text-slate-600 dark:text-slate-400">{menge(p.menge, p.einheit)}</td>
                    {mitPreisen && <>
                      <td className="py-2 text-right text-slate-600 dark:text-slate-400">{preis(p.einzelpreis)}</td>
                      <td className="py-2 text-right text-slate-900 dark:text-slate-100">{euro(p.netto)}</td>
                    </>}
                  </tr>
                ))}
              </tbody>
            </table>

            {mitPreisen && (
              <div className="space-y-1 border-t border-slate-200 pt-3 text-sm dark:border-slate-800">
                <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>{t('feld.netto')}</span><span>{euro(offen.netto)}</span></div>
                <div className="flex justify-between text-slate-600 dark:text-slate-400"><span>{profil().steuerName}</span><span>{euro(offen.steuer)}</span></div>
                <div className="flex justify-between text-base font-bold text-slate-900 dark:text-slate-100">
                  <span>{t('feld.gesamt')}</span><span>{euro(offen.brutto)}</span>
                </div>
              </div>
            )}

            {offen.nachfolger.length > 0 && (
              <Hinweis art="info">
                Folgebelege: {offen.nachfolger.map(n => `${BELEGNAME[n.art] ?? n.art} ${n.nummer ?? ''}`.trim()).join(', ')}
              </Hinweis>
            )}

            <div className="flex flex-wrap gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
              <Knopf onClick={() => drucken(offen)}>{t('belege.druckenPdf')}</Knopf>
              {(NAECHSTE_SCHRITTE[offen.art]?.[offen.status] ?? []).map(s => (
                <Knopf key={s.status} variante={s.gefahr ? 'gefahr' : 'primaer'}
                       onClick={() => statusSetzen(offen, s.status, s.gefahr)}>
                  {s.text}
                </Knopf>
              ))}
              {offen.status !== 'storniert' && offen.status !== 'abgelehnt' && UMWANDLUNGEN[offen.art].map(u => (
                <Knopf key={u.ziel} onClick={() => umwandeln(offen, u.ziel)}
                       disabled={u.ziel === 'rechnung' && !!offen.rechnungId}>
                  {u.ziel === 'rechnung' && offen.rechnungId ? 'Bereits berechnet' : u.text}
                </Knopf>
              ))}
              {!offen.nummer && (
                <Knopf variante="gefahr" onClick={() => loeschen(offen)}>{t('belege.entwurfLoeschen')}</Knopf>
              )}
            </div>
          </div>
        </Fenster>
      )}

      <Karte>
        <KartenKopf titel={reiter.titel} hinweis={liste ? `${liste.length} Belege` : undefined} />
        {laedt && !liste ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : !liste || liste.length === 0 ? (
          <LeerZustand
            titel={`Noch keine ${reiter.titel}`}
            text={art === 'angebot'
              ? 'Ein Angebot zeigt dem Kunden vorab, was die Leistung kostet — unverbindlich und mit Frist.'
              : art === 'auftrag'
                ? 'Sagt der Kunde zum Angebot Ja, wird daraus die verbindliche Auftragsbestätigung.'
                : art === 'lieferschein'
                  ? 'Der Lieferschein liegt beim Paket und zeigt ohne Preise, was geliefert wird.'
                  : 'Gutschriften entstehen aus einer Rechnung oder Retoure.'}
          />
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-slate-800">
            {liste.map(b => (
              <li key={b.id}>
                <button onClick={() => setOffen(b)}
                        className="flex w-full flex-wrap items-center gap-3 px-5 py-3.5 text-start hover:bg-slate-50 dark:hover:bg-slate-800/60">
                  <div className="min-w-[200px] flex-1">
                    <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {b.nummer || 'Entwurf'} · {b.kundeName}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      {datum(b.datum)} · {b.positionen.length} {b.positionen.length === 1 ? 'Position' : 'Positionen'}
                      {b.art === 'angebot' && b.gueltigBis ? ` · gültig bis ${datum(b.gueltigBis)}` : ''}
                      {b.rechnungId ? ' · berechnet' : ''}
                    </p>
                  </div>
                  <Abzeichen art={STATUS[b.status]?.farbe ?? 'neutral'}>{STATUS[b.status]?.text ?? b.status}</Abzeichen>
                  {mitPreisen && (
                    <span className="w-24 text-end font-medium text-slate-900 dark:text-slate-100">{euro(b.brutto)}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Karte>
    </div>
  );
}

function Kennzahl({ titel, wert, zusatz }: { titel: string; wert: string; zusatz: string }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
      <p className="text-xs text-slate-500 dark:text-slate-400">{titel}</p>
      <p className="mt-1 text-xl font-bold text-slate-900 dark:text-slate-100">{wert}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400">{zusatz}</p>
    </div>
  );
}
